import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import truncate from 'lodash/truncate';
import styles from '@components/SingleArticleInspirations/SingleArticleInspirations.styles';
import Heading from '@components/Heading/Heading';
import { ICmsDocument } from '@features/cms/typescript';
import ProjectFeatures from '@components/ProjectFeatures/ProjectFeatures';
import VoxboxWidget from '@features/cms/components/collections/VoxboxWidget/VoxboxWidget';
import { stripBrTags } from '@utils/splitTextByLineBreaks';
import ResponsiveImage from '@components/ResponsiveImage/ResponsiveImage';

type PropsType = {
  article: ICmsDocument;
  isInterior: boolean;
  buttonHistoryBack?: boolean;
  onClick?: () => void;
  onAuxClick?: () => void;
};

const SingleArticleInspirations = ({
  article,
  isInterior,
  buttonHistoryBack = false,
  onClick = undefined,
  onAuxClick = undefined,
}: PropsType) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const linkRef = useRef<HTMLAnchorElement | null>(null);
  const { baseData } = article;
  const { t } = useTranslation('common');
  const handleButtonHistoryBack = () => (buttonHistoryBack ? '?button-history-back' : '');
  const articleTitle = stripBrTags(baseData?.name);

  // WCAG 2.4.7 Focus Visible - klasa na kontenerze gdy link ma focus-visible
  useEffect(() => {
    const container = containerRef.current;
    const link = linkRef.current;
    if (!container || !link) return;

    const handleFocusIn = () => {
      if (link.matches(':focus-visible')) {
        container.classList.add('has-focus-visible');
      }
    };

    const handleFocusOut = () => {
      container.classList.remove('has-focus-visible');
    };

    link.addEventListener('focusin', handleFocusIn);
    link.addEventListener('focusout', handleFocusOut);

    return () => {
      link.removeEventListener('focusin', handleFocusIn);
      link.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  return (
    <div sx={styles.singleArticle(isInterior)} ref={containerRef} data-testid="SingleArticleInspirations">
      {baseData && (
        <>
          {isInterior && (
            <VoxboxWidget variant="dark" hoverRef={containerRef} small url={baseData.projectUrl || undefined} />
          )}
          <Link
            ref={linkRef}
            href={`/inspirations/${isInterior ? 'interior-designs' : 'ideas'}/${
              baseData.slug
            }${handleButtonHistoryBack()}`}
            onClick={onClick}
            onAuxClick={onAuxClick}
            data-testid="SingleArticleInspirations-link"
          >
            <div sx={styles.wrapper} data-testid="SingleArticleInspirations-wrapper">
              <div sx={styles.imgContainer(isInterior)} data-testid="SingleArticleInspirations-imgContainer">
                <ResponsiveImage image={baseData.image || ''} alt={articleTitle} />
                {isInterior && baseData?.imageHover && (
                  <ResponsiveImage
                    sx={styles.imgContainer(isInterior).imageHover}
                    className="image_hover"
                    image={baseData.imageHover || ''}
                    alt={articleTitle}
                  />
                )}
              </div>
              <div sx={styles.headingContainer} data-testid="SingleArticleInspirations-headingContainer">
                <Heading addSx={styles.heading} h={4} headingType={4}>
                  {articleTitle}
                </Heading>
                {baseData?.tags && (
                  <div sx={styles.tag}>
                    {Object.values(baseData.tags).map((tag) => (
                      <span key={tag}>{`#${tag}`}</span>
                    ))}
                  </div>
                )}
              </div>
              {isInterior ? (
                <ProjectFeatures
                  surface={baseData.surface}
                  furniture={baseData.furniture}
                  doors={baseData.doors}
                  floor={baseData.floor}
                  walls={baseData.walls}
                  rows={false}
                  isListing
                />
              ) : (
                <div sx={styles.description}>
                  {truncate(baseData.description || '', {
                    length: 140,
                    omission: '...',
                    separator: ' ',
                  })}
                </div>
              )}
              <div sx={styles.readMore} className="readMore">
                {isInterior ? t('readMoreInterior') : t('readMore')}
              </div>
            </div>
          </Link>
        </>
      )}
    </div>
  );
};

export default SingleArticleInspirations;
